<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <div class="lookup-header">
        <div class="lookup-header__copy">
          <div class="lookup-title">Config</div>
          <div class="lookup-subtitle">Maintain general time definitions and measurements.</div>
        </div>
        <v-text-field
            v-model="search"
            class="lookup-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search"
            single-line
        ></v-text-field>
      </div>
      <v-data-table
          :headers="headers"
          :items="generalTimes"
          :loading="loading"
          :search="search"
          class="lookup-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.name`]="{ item }">
          <label for="item">{{ item.name ? item.name : "-" }}</label>
        </template>
        <template v-slot:[`item.code`]="{ item }">
          <label for="item">{{ item.code ? item.code : "-" }}</label>
        </template>
        <template v-slot:[`item.duration`]="{ item }">
          <label for="item">{{ item.duration !== null && item.duration !== undefined ? item.duration : "-" }}</label>
        </template>
        <template v-slot:[`item.measurement`]="{ item }">
          <label for="item">{{ item.measurement ? item.measurement : "-" }}</label>
        </template>
        <template v-if="canEditGeneralTime" v-slot:[`item.actions`]="{ item }">
          <v-tooltip v-if="canEditGeneralTime" bottom>
            <template v-slot:activator="{ on }">
              <v-icon class="mr-2 lookup-action-icon" @click="editGeneralTime(item)" v-on="on">
                mdi-circle-edit-outline
              </v-icon>
            </template>
            <span>Edit Config</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="50%">
        <v-card class="lookup-dialog-card">
          <v-card-title class="lookup-dialog__title" primary-title>{{ title }}
            <v-spacer></v-spacer>
            <v-btn class="lookup-dialog__close" color="white" text @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <AddGeneralTime
              v-if="dialog"
              :generalTime="generalTime"
              @close="closeDialog()"
              @registerOrUpdateGeneralTime="registerOrUpdateGeneralTime"
          ></AddGeneralTime>
        </v-card>
      </v-dialog>
    </template>

  </div>
</template>

<script>
import {getUser} from "@/utils/helper";
import {LookupService} from "@/common/api.service";
import {GeneralTimeService} from "./generalTime.service";
import AddGeneralTime from "./add-general-time.vue";

export default {
  components: {
    AddGeneralTime,
  },
  data() {
    return {
      search: "",
      loading: false,
      headers: [
        {text: "Config Name", value: "name"},
        {text: "Config Code", value: "code"},
        {text: "Duration", value: "duration"},
        {text: "Measurement", value: "measurement"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      generalTimes: [],
      generalTime: null,
      dialog: false,
      title: "",
    };
  },
  computed: {
    canEditGeneralTime() {
      return this.$can("config","edit",this.isSystemAdmin);
    },
    isSystemAdmin() {
      return getUser()?.data?.userRoles?.roleName === "System admins";
    },
  },
  async beforeMount() {
    this.fetchGeneralTimes();
  },
  methods: {
    async fetchGeneralTimes() {
      this.loading = true;
      const res = await LookupService.getGeneralTimes();
      if (res.status === 200) {
        this.generalTimes = res.data;
        this.loading = false;
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching general times, Please try Again!"
        );
      }
    },
    editGeneralTime(item) {
      this.generalTime = item;
      this.dialog = true;
      this.title = "Edit Config";
    },
    closeDialog() {
      this.dialog = false;
      this.fetchGeneralTimes();
    },
    async registerOrUpdateGeneralTime(generalTime) {
      this.loading = true;
      let res = null;

      if (generalTime.editMode) {
        res = await GeneralTimeService.update(
            generalTime.id,
            generalTime
        );

        if (res.status === 201) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchGeneralTimes();
              this.loading = false;
              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;
              break;
          }
        } else {
          this.$toast.error(
              "Unable to update general time, please try again"
          );
          this.loading = false;
        }
      } else {
        res = await GeneralTimeService.create(generalTime);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchGeneralTimes();
              this.loading = false;
              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;
              break;
          }
        } else {
          this.$toast.error(
              "Unable to create general time, please try again"
          );
          this.loading = false;
        }
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.lookup-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.lookup-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.lookup-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.lookup-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lookup-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.lookup-subtitle {
  font-size: 14px;
  color: #475569;
}

.lookup-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.lookup-table {
  border-radius: 0 0 12px 12px;
}

.lookup-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.lookup-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.lookup-dialog__close {
  color: #ffffff !important;
}

.lookup-dialog__close .v-icon {
  color: #ffffff !important;
}

.lookup-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.lookup-action-icon {
  color: #1976d2 !important;
}

.lookup-action-icon--danger {
  color: #dc2626 !important;
}

@media (max-width: 700px) {
  .lookup-page {
    padding: 12px;
  }

  .lookup-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .lookup-search {
    max-width: 100%;
  }

  .lookup-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
